<template>
  <form
    id="icon-add-styling"
    action=""
    class="icon-details-add-styling margin-bottom-2xl laptop:margin-top-2xl laptop:margin-bottom-0"
    style="--color-input-min-width: var(--icon-details-input-color-min-width)"
    @submit.prevent
  >
    <div
      class="icon-details-styling-color"
      style="--input-padding: var(--spacing-xs); --input-border-color: var(--fa-gravy); --input-box-shadow: none; gap: var(--spacing-3xs)"
    >
      <div class="size-xs padding-right-md padding-bottom-xs">
        <span
          class="text-uppercase padding-right-sm"
          style="letter-spacing: var(--letter-spacing-3xl); font-weight: var(--font-weight-black)"
          >Color</span
        >
        <site-link
          v-if="isDuotoneFamily(styling.family)"
          :to="getDocsPath('/web/style/custom#add-your-own-custom-styling-with-css', { version: majorVersion })"
          aria-label="Use our custom color and opacity CSS properties too adjust the color of Duotone icon layers. Click to learn more"
          data-balloon-pos="right"
          data-balloon-length="xlarge"
          style="--link-color: var(--fa-gravy)"
        >
          <font-awesome-icon
            :icon="$v7.solid.faInfoCircle"
            size="lg"
          />
        </site-link>
        <site-link
          v-else
          :to="getDocsPath('/web/style/custom#add-your-own-custom-styling-with-css', { version: majorVersion })"
          aria-label="Use basic CSS style to add color to an icon. Click to learn more"
          data-balloon-pos="right"
          data-balloon-length="xlarge"
          style="--link-color: var(--fa-gravy)"
        >
          <font-awesome-icon :icon="$v7.solid.faInfoCircle" />
        </site-link>
      </div>

      <template v-if="isDuotoneFamily(styling.family)">
        <ul
          class="custom-radio-button-set display-flex align-stretch"
          style="--list-style-type: none; --padding-left: 0; --list-margin-top: 0; flex-grow: 1; gap: var(--spacing-xs)"
        >
          <li>
            <label
              for="color-none"
              class="margin-0 padding-y-xs padding-x-sm"
              style="border-radius: var(--border-radius-md); flex-grow: 0; background-color: var(--fa-lt-gravy)"
              aria-label="Remove Coloring"
              data-balloon-pos="up"
            >
              <font-awesome-icon
                :icon="$v7.solid.faBan"
                style="vertical-align: middle"
              />
              <span class="sr-only">Default Color</span>
            </label>
            <input
              id="color-none"
              type="radio"
              name="icon-color"
              value=""
              style="appearance: none; position: absolute; box-shadow: none"
              @click="resetDuotone()"
            />
          </li>
          <li
            aria-label="Primary Color"
            data-balloon-pos="up"
            :style="primaryColorStyle"
          >
            <label
              for="styling-color-primary"
              class="sr-only"
            >
              Icon Primary Color
            </label>
            <input
              id="styling-color-primary"
              :value="styling.colorPrimary"
              type="color"
              class="styling-color-input margin-bottom-0"
              style="--input-padding: var(--spacing-4xs); min-width: var(--color-input-min-width)"
              @change="(event) => change('colorPrimary', event.target.value)"
            />
            <!-- FYI: needed to give more vertical padding in smaller viewports  -->
            <span
              class="input-color-shim tablet:display-none position-relative z--1"
              aria-hidden="true"
              style="opacity: 0"
              >Choose a color</span
            >
          </li>

          <li
            v-if="isDuotoneFamily(styling.family)"
            aria-label="Primary Opacity"
            data-balloon-pos="up"
            style="max-width: 4rem; margin-right: var(--spacing-4xs)"
          >
            <label
              for="styling-color-primary-opacity"
              class="sr-only"
            >
              Icon Primary Opacity
            </label>
            <input
              id="styling-color-primary-opacity"
              :value="styling.opacityPrimary"
              type="number"
              placeholder="0 to 1.0"
              min="0"
              max="1.0"
              step="0.1"
              class="styling-color-input simple margin-bottom-0 text-center"
              @input="(event) => change('opacityPrimary', event.target.value)"
            />
          </li>

          <li
            class="margin-left-3xs"
            aria-label="Secondary Color"
            data-balloon-pos="up"
            style="flex-grow: 1"
          >
            <label
              for="styling-color-secondary"
              class="sr-only"
            >
              Icon Secondary Color
            </label>
            <input
              id="styling-color-secondary"
              :value="styling.colorSecondary"
              type="color"
              class="styling-color-input margin-bottom-0"
              :style="`--input-padding: var(--spacing-4xs); min-width: var(--color-input-min-width)`"
              @change="(event) => change('colorSecondary', event.target.value)"
            />

            <!-- FYI: needed to give more vertical padding in smaller viewports  -->
            <span
              class="input-color-shim tablet:display-none position-relative z--1"
              aria-hidden="true"
              style="opacity: 0"
              >Choose a color</span
            >
          </li>

          <li
            aria-label="Secondary Opacity"
            data-balloon-pos="up"
            style="max-width: 4rem"
          >
            <label
              for="styling-color-secondary-opacity"
              class="sr-only"
            >
              Icon Secondary Opacity
            </label>
            <input
              id="styling-color-secondary-opacity"
              :value="styling.opacitySecondary"
              type="number"
              min="0"
              max="1.0"
              step="0.1"
              placeholder="0 to 1.0"
              class="styling-color-input simple margin-bottom-0 text-center"
              @input="(event) => change('opacitySecondary', event.target.value)"
            />
          </li>
        </ul>
      </template>
      <template v-else>
        <ul
          class="custom-radio-button-set display-flex align-stretch"
          style="--list-style-type: none; --padding-left: 0; --list-margin-top: 0; flex-grow: 1; gap: var(--spacing-xs)"
        >
          <li
            v-for="colorSelect in iconColors"
            :key="colorSelect.label"
            :style="radioCustomStyles(colorSelect.color)"
            class="position-relative"
          >
            <template v-if="colorSelect.color === null">
              <input
                id="color-none"
                type="radio"
                name="icon-color"
                value=""
                style="appearance: none; position: absolute; box-shadow: none"
                :checked="colorSelect.color === styling.colorPrimary"
                @click="colorReset('colorPrimary')"
              />
              <label
                for="color-none"
                class="styling-color-input margin-0 padding-y-xs padding-x-sm"
                style="border-radius: var(--border-radius-md); flex-grow: 0; background-color: var(--fa-lt-gravy)"
                aria-label="Remove Coloring"
                data-balloon-pos="up"
              >
                <font-awesome-icon
                  :icon="$v7.solid.faBan"
                  style="vertical-align: middle"
                />
                <span class="sr-only">Default Color</span>
              </label>
            </template>
            <template v-else>
              <input
                :id="`color-${colorSelect.label}`"
                type="radio"
                name="icon-color"
                style="appearance: none; position: absolute; box-shadow: none"
                :value="colorSelect.color"
                :checked="colorSelect.color === styling.colorPrimary"
                @click="change('colorPrimary', colorSelect.color)"
              />
              <label
                :for="`color-${colorSelect.label}`"
                class="styling-color-input margin-0 padding-4xs"
                style="border-radius: var(--border-radius-md); flex-grow: 1"
                :aria-label="`Color it ${colorSelect.label}`"
                data-balloon-pos="up"
              >
                <span
                  class="display-inline-block"
                  :style="`height: 100%;width: 100%;border-radius: var(--border-radius-sm);vertical-align: middle;background-color: ${colorSelect.color};`"
                ></span>
                <span class="sr-only">{{ colorSelect.label }}</span>
              </label>
            </template>
          </li>
          <li
            :style="radioCustomStyles()"
            :class="`position-relative ${hasCustomColor ? 'active' : ''}`"
          >
            <label
              for="color-custom"
              class="sr-only"
              style="border: 0"
              aria-label="Pick your own color"
              data-balloon-pos="up"
            >
              <input
                id="color-custom"
                type="radio"
                name="icon-color"
                value="color-custom"
                class="sr-only"
                @click="openCustomColor()"
              />
              Pick Your Own Color
            </label>
            <input
              id="color-custom"
              ref="colorCustom"
              :value="styling.colorPrimary"
              type="color"
              class="margin-bottom-0"
              style="
                appearance: none;
                position: absolute;
                box-shadow: none;
                --input-padding: var(--spacing-4xs);
                height: 100%;
                min-width: var(--color-input-min-width);
                opacity: 0;
              "
              @change="(event) => change('colorPrimary', event.target.value)"
            />
            <label
              for="color-custom"
              class="styling-color-input margin-0 padding-4xs"
              style="border-radius: var(--border-radius-md); flex-grow: 1"
              aria-label="Pick your own color"
              data-balloon-pos="up"
            >
              <span
                class="display-inline-block"
                :style="colorCustomStyles"
              ></span>
              <span class="sr-only">Pick a color</span>
            </label>
          </li>
        </ul>
      </template>
    </div>

    <div class="icon-details-styling-size">
      <div class="size-xs padding-right-md padding-bottom-xs">
        <span
          class="text-uppercase padding-right-sm"
          style="letter-spacing: var(--letter-spacing-3xl); font-weight: var(--font-weight-black)"
          >Size</span
        ><site-link
          :to="getDocsPath('/web/style/size', { version: majorVersion })"
          aria-label="With our web toolkit, you can easily size an icon with a CSS class. Click to learn more"
          data-balloon-pos="right"
          data-balloon-length="xlarge"
          style="--link-color: var(--fa-gravy)"
        >
          <font-awesome-icon :icon="$v7.solid.faInfoCircle" />
        </site-link>
      </div>

      <ul
        class="custom-radio-button-set display-flex align-stretch"
        style="--list-style-type: none; --padding-left: 0; --list-margin-top: 0; flex-grow: 1; gap: var(--spacing-xs)"
      >
        <li
          v-for="sizeSelect in iconSizes"
          :key="sizeSelect.name"
          :style="radioCustomStyles(sizeSelect.value)"
          class="position-relative"
        >
          <template v-if="sizeSelect.value === null">
            <input
              id="size-none"
              type="radio"
              name="icon-size"
              value=""
              style="appearance: none; position: absolute; box-shadow: none"
              @change="change('size', sizeSelect.value)"
            />
            <label
              for="size-none"
              class="margin-0 padding-y-xs padding-x-sm"
              style="border-radius: var(--border-radius-md); flex-grow: 0; background-color: var(--fa-lt-gravy)"
              aria-label="Remove Sizing"
              data-balloon-pos="up"
            >
              <font-awesome-icon
                :icon="$v7.solid.faBan"
                style="vertical-align: middle"
              />
              <span class="sr-only">{{ sizeSelect.name }}</span>
            </label>
          </template>
          <template v-else>
            <input
              :id="`size-${sizeSelect.value}`"
              type="radio"
              name="icon-size"
              :value="sizeSelect.value"
              style="appearance: none; position: absolute; box-shadow: none"
              :checked="sizeSelect.value === styling.size"
              @change="change('size', sizeSelect.value)"
            />
            <label
              :for="`size-${sizeSelect.value}`"
              class="margin-0 padding-y-xs padding-x-sm"
              style="border-radius: var(--border-radius-md); flex-grow: 1"
            >
              <span>{{ sizeSelect.name }}</span>
            </label>
          </template>
        </li>
      </ul>
    </div>

    <div :class="`icon-details-styling-rotate position-relative ${hasAnimation ? 'disabled' : ''}`">
      <span
        v-if="hasAnimation"
        aria-label="Can't rotate while animating"
        data-balloon-pos="up-left"
        style="position: absolute; inset: 0; cursor: not-allowed"
      ></span>
      <div class="size-xs padding-right-md padding-bottom-xs">
        <span
          class="text-uppercase padding-right-sm"
          style="letter-spacing: var(--letter-spacing-3xl); font-weight: var(--font-weight-black)"
          >Rotate</span
        ><site-link
          :to="getDocsPath('/web/style/rotate', { version: majorVersion })"
          aria-label="With our web toolkit, you can easily rotate an icon with a CSS class. Click to learn more"
          data-balloon-pos="right"
          data-balloon-length="xlarge"
          style="--link-color: var(--fa-gravy)"
        >
          <font-awesome-icon :icon="$v7.solid.faInfoCircle" />
        </site-link>
      </div>

      <ul
        :class="`custom-radio-button-set display-flex align-stretch ${hasAnimation ? 'disabled' : ''}`"
        style="--list-style-type: none; --padding-left: 0; --list-margin-top: 0; flex-grow: 1; gap: var(--spacing-xs)"
      >
        <li
          v-for="rotateSelect in iconRotations"
          :key="rotateSelect.name"
          :style="radioCustomStyles(rotateSelect.value)"
          class="position-relative"
          @click="change('rotation', rotateSelect.value)"
        >
          <template v-if="rotateSelect.value === null">
            <input
              id="rotate-none"
              type="radio"
              name="icon-rotate"
              value=""
              style="appearance: none; position: absolute; box-shadow: none"
            />
            <label
              for="rotate-none"
              class="margin-0 padding-y-xs padding-x-sm"
              style="border-radius: var(--border-radius-md); flex-grow: 0; background-color: var(--fa-lt-gravy)"
              aria-label="Remove Rotation"
              data-balloon-pos="up"
            >
              <font-awesome-icon
                :icon="$v7.solid.faBan"
                style="vertical-align: middle"
              />
              <span class="sr-only">{{ rotateSelect.name }}</span>
            </label>
          </template>
          <template v-else>
            <input
              :id="`${rotateSelect.value}`"
              type="radio"
              name="icon-rotate"
              :value="rotateSelect.value"
              :checked="rotateSelect.value === styling.rotation"
              style="appearance: none; position: absolute; box-shadow: none"
            />
            <label
              :for="`${rotateSelect.value}`"
              class="margin-0 padding-y-xs padding-x-sm"
              style="border-radius: var(--border-radius-md); flex-grow: 1"
              :aria-label="`Rotate ${rotateSelect.name}deg to the right`"
              data-balloon-pos="up"
            >
              <span>{{ rotateSelect.name }}&deg;</span>
            </label>
          </template>
        </li>
        <li
          v-if="supportsCustomRotation"
          style="padding-right: var(--spacing-4xs); flex-grow: 1; display: flex; text-align: center"
          :class="`position-relative ${hasCustomRotation ? 'active' : ''}`"
        >
          <input
            id="rotate-custom"
            ref="rotateCustom"
            type="radio"
            name="icon-rotate"
            value="rotate-custom"
            class="sr-only"
            :checked="customRotation === styling.rotation"
          />
          <label
            for="rotate-custom"
            class="sr-only margin-0 padding-y-xs padding-x-sm"
          >
            Rotate custom
          </label>
          <input
            id="rotate-custom"
            :value="customRotation"
            class="margin-0 text-center"
            type="number"
            size="5"
            name="icon-rotate-custom"
            placeholder="Custom..."
            style="flex-grow: 1; --input-border-color: var(--fa-lt-gravy); --input-padding: var(--spacing-xs) var(--spacing-sm); --input-box-shadow: none"
            @change="(event) => applyCustomRotation(event.target.value)"
          />
        </li>
      </ul>
    </div>

    <div :class="`icon-details-styling-flip position-relative ${hasAnimation ? 'disabled' : ''}`">
      <span
        v-if="hasAnimation"
        aria-label="Can't flip while animating"
        data-balloon-pos="up-left"
        style="position: absolute; inset: 0; cursor: not-allowed"
      ></span>
      <div class="size-xs padding-right-md padding-bottom-xs">
        <span
          class="text-uppercase padding-right-sm"
          style="letter-spacing: var(--letter-spacing-3xl); font-weight: var(--font-weight-black)"
          >Flip</span
        ><site-link
          :to="getDocsPath('/web/style/rotate', { version: majorVersion })"
          aria-label="With our web toolkit, you can easily flip an icon with a CSS class. Click to learn more"
          data-balloon-pos="right"
          data-balloon-length="xlarge"
          style="--link-color: var(--fa-gravy)"
        >
          <font-awesome-icon :icon="$v7.solid.faInfoCircle" />
        </site-link>
      </div>
      <ul
        :class="`custom-radio-button-set display-flex align-stretch ${hasAnimation ? 'disabled' : ''}`"
        style="--list-style-type: none; --padding-left: 0; --list-margin-top: 0; flex-grow: 1; gap: var(--spacing-xs)"
      >
        <li
          v-for="flipSelect in iconFlips"
          :key="flipSelect.name"
          :style="radioCustomStyles(flipSelect.value)"
          class="position-relative"
          @click="change('rotation', flipSelect.value)"
        >
          <template v-if="flipSelect.value === null">
            <input
              id="flip-none"
              type="radio"
              name="icon-flip"
              value=""
              style="appearance: none; position: absolute; box-shadow: none"
            />
            <label
              for="flip-none"
              class="margin-0 padding-y-xs padding-x-sm"
              style="border-radius: var(--border-radius-md); flex-grow: 0; background-color: var(--fa-lt-gravy)"
              aria-label="Remove Flipping"
              data-balloon-pos="up"
            >
              <font-awesome-icon
                :icon="$v7.solid.faBan"
                style="vertical-align: middle"
              />
              <span class="sr-only">{{ flipSelect.name }}</span>
            </label>
          </template>
          <template v-else>
            <input
              :id="`${flipSelect.id}`"
              type="radio"
              name="icon-flip"
              :value="flipSelect.value"
              style="appearance: none; position: absolute; box-shadow: none"
              :checked="flipSelect.value === styling.rotation"
            />
            <label
              :for="`${flipSelect.id}`"
              class="margin-0 padding-y-xs padding-x-sm"
              style="border-radius: var(--border-radius-md); flex-grow: 1"
              :aria-label="`${flipSelect.name}`"
              data-balloon-pos="up"
            >
              <font-awesome-icon
                :icon="$v7[flipSelect.family][flipSelect.icon]"
                size="lg"
              />
              <span class="sr-only">{{ flipSelect.name }}</span>
            </label>
          </template>
        </li>
      </ul>
    </div>
  </form>
</template>

<script>
  import {
    animationOptions,
    familyOptions,
    flippingOptions,
    isDuotoneFamily,
    rotationOptions,
    sizeOptions,
    DUOTONE_FAMILY_STARTING_VERSION,
    STYLING_ALL_SIZES_STARTING_VERSION,
    STYLING_CUSTOM_ROTATION_STARTING_VERSION,
    getMajorVersion
  } from 'app/releases'
  import { DocsMixin } from 'app/mixins'
  import { DEFAULT_STYLING } from 'app/views/icons/defaults'

  export default {
    name: 'IconStyling',

    mixins: [DocsMixin],

    props: {
      defaultStyles: {
        type: Object
      },
      styling: {
        type: Object,
        required: true
      },
      majorVersion: {
        type: Number
      },
      icon: {
        type: Object
      }
    },

    data() {
      return {
        iconColors: [
          { label: 'none', color: null },
          { label: 'yellow', color: '#FFD43B' },
          { label: 'teal', color: '#63E6BE' },
          { label: 'blue', color: '#74C0FC' },
          { label: 'violet', color: '#B197FC' }
        ]
      }
    },

    computed: {
      iconName() {
        return this.icon ? this.icon.name : ''
      },

      isNotShowingFamilyAndStyles() {
        return this.styling.family === 'brands' || this.styling.style === 'custom'
      },

      hasCustomColor() {
        if (this.styling.colorPrimary !== DEFAULT_STYLING.colorPrimary) {
          const foundExisting = this.iconColors.filter((c) => c.color === this.styling.colorPrimary)
          return foundExisting.length === 0
        } else {
          return false
        }
      },

      hasAnimation() {
        const animation = this.styling.animation
        if (animation == null) {
          return false
        } else {
          return true
        }
      },

      hasCustomRotation() {
        const rotate = parseInt(this.styling.rotation)
        return Number.isInteger(rotate)
      },

      customRotation() {
        return this.hasCustomRotation ? this.styling.rotation : ''
      },

      iconAnimations() {
        return animationOptions(this.majorVersion)
      },

      iconFamilies() {
        return familyOptions(this.majorVersion)
      },

      iconRotations() {
        return rotationOptions()
      },

      iconFlips() {
        return flippingOptions()
      },

      iconSizes() {
        let sizes = []

        if (this.majorVersion < getMajorVersion(STYLING_ALL_SIZES_STARTING_VERSION)) {
          sizes = sizeOptions(this.majorVersion).slice(0, 4)
        } else {
          sizes = sizeOptions(this.majorVersion).slice(0, 7)
        }

        return sizes
      },

      primaryColorStyle() {
        if (this.styling.style !== 'duotone') {
          return 'flex: 1 0 auto'
        } else {
          return 'flex-grow: 1'
        }
      },

      animateSelectCustomStyle() {
        if (this.styling.rotation !== null) {
          return {
            '--input-with-icon-color': 'var(--input-disabled-color)'
          }
        } else {
          return {
            '--input-with-icon-color': 'var(--text-color)'
          }
        }
      },

      colorCustomStyles() {
        let style = {}

        if (this.hasCustomColor) {
          style['background'] = this.styling.colorPrimary
        } else {
          style['background'] = 'linear-gradient(50deg, var(--fa-red) 0%, var(--fa-yellow) 30%, var(--fa-teal) 60%, var(--fa-blue) 100%)'
        }

        return {
          ...style,
          ...{
            'height': '100%',
            'width': '100%',
            'border-radius': 'var(--border-radius-sm)',
            'vertical-align': 'middle'
          }
        }
      },

      rotateSelectCustomStyle() {
        if (this.styling.animation !== null) {
          return {
            '--input-with-icon-color': 'var(--input-disabled-color)'
          }
        } else {
          return {
            '--input-with-icon-color': 'var(--text-color)'
          }
        }
      },

      selectCustomStyle() {
        return {
          '--input-with-icon-color': 'var(--text-color)'
        }
      },

      selectCustomSelectStyle() {
        return {
          '--input-border-color': 'var(--fa-gravy)',
          'font-weight': 'var(--font-weight-normal)',
          'padding-top': 'var(--spacing-sm)',
          'padding-bottom': 'var(--spacing-sm)'
        }
      },

      supportsCustomRotation() {
        return this.majorVersion >= getMajorVersion(STYLING_CUSTOM_ROTATION_STARTING_VERSION)
      }
    },

    methods: {
      isDuotoneFamily(family) {
        if (this.majorVersion < getMajorVersion(DUOTONE_FAMILY_STARTING_VERSION)) {
          return this.styling.style === 'duotone'
        } else {
          return isDuotoneFamily(family)
        }
      },

      change(attr, value) {
        this.$posthog.capture('icon_preview_styling_update', {
          attribute: attr,
          value: value,
          icon_name: this.iconName,
          icon_style: this.styling.style,
          icon_family: this.styling.family
        })

        // This links primary and secondary colors UNTIL secondary color is changed.
        if (attr === 'colorPrimary' && this.styling.colorSecondary === this.styling.colorPrimary) {
          return this.$emit('change', { ...this.styling, ...{ [attr]: value, ['colorSecondary']: value } })
        } else {
          return this.$emit('change', { ...this.styling, ...{ [attr]: value } })
        }
      },

      applyCustomRotation(value) {
        if (!isNaN(value)) {
          this.$refs.rotateCustom.click()
          this.change('rotation', value)
        }
      },

      openCustomColor() {
        // when clicking the radio button we want to open the color control named: colorCustom
        this.$refs.colorCustom.click()
      },

      colorReset(primaryColor) {
        this.$posthog.capture('icon_preview_styling_update', {
          color: primaryColor
        })
        if (primaryColor === 'colorPrimary') {
          return this.$emit('change', {
            ...this.styling,
            ...{ [primaryColor]: DEFAULT_STYLING.colorPrimary, ['colorSecondary']: DEFAULT_STYLING.colorSecondary }
          })
        }
      },

      resetDuotone() {
        if (
          this.styling.colorPrimary !== DEFAULT_STYLING.colorPrimary ||
          this.styling.colorSecondary !== DEFAULT_STYLING.colorSecondary ||
          this.styling.opacityPrimary !== DEFAULT_STYLING.opacityPrimary ||
          this.styling.opacitySecondary !== DEFAULT_STYLING.opacitySecondary
        )
          return this.$emit('change', {
            ...this.styling,
            ...{
              ['colorPrimary']: DEFAULT_STYLING.colorPrimary,
              ['colorSecondary']: DEFAULT_STYLING.colorSecondary,
              ['opacityPrimary']: DEFAULT_STYLING.opacityPrimary,
              ['opacitySecondary']: DEFAULT_STYLING.opacitySecondary
            }
          })
      },

      radioCustomStyles(value) {
        let style = {}

        if (value === null) {
          style['flex-grow'] = '0'
        } else {
          style['flex-grow'] = '1'
        }

        return {
          ...style,
          ...{
            'display': 'flex',
            'text-align': 'center'
          }
        }
      },

      styleSelectButtonStyle(iconStyle) {
        let style = {}

        if (iconStyle === this.styling.style) {
          style['--button-hover-color'] = 'var(--text-color)'
          style['--button-hover-background'] = 'var(--fa-gravy)'
        } else {
          style['--button-hover-color'] = 'var(--link-hover-color)'
          style['--button-hover-background'] = 'transparent'
        }

        return {
          ...style,
          ...{
            '--button-border-radius': 'var(--input-border-radius)',
            '--button-border-color': 'var(--fa-gravy)',
            '--button-box-shadow': 'none',
            '--button-box-shadow-width': '0',
            '--button-padding': 'var(--spacing-sm) var(--spacing-lg)',
            '--button-color': 'var(--link-color)',
            '--button-active-border-color': 'var(--fa-gravy)',
            '--button-active-background': 'var(--fa-gravy)',
            '--button-active-color': 'var(--text-color)'
          }
        }
      }
    }
  }
</script>
